import { a3Copy } from "@/components/assignments/a3-copy";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { AssignmentTimezone, useAdmittedWords } from "@/components/assignments/assignment-workspace-context";
import { shutWords } from "./hand-in-model";
import { useAssignmentDraft } from "@/components/assignments/use-assignment-draft";
import { invalidateAssignment } from "@/components/assignments/invalidate-assignment";
import type { WritingTaskController } from "@/components/assignments/NativeWriting";
import type { Blocker, Result } from "@/components/ui/box.schema";
import { assignmentsApi, type ArtifactUploadArrival, type AssignmentDetail, type ReferenceDocument, type ReferenceDraft, type Submission } from "@/lib/api/assignments";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import { useReferenceDraft } from "./use-reference-draft";
import { acceptAttribute, admitFile, uploadLimits } from "./upload-policy";
import { useHandInDriveChooser } from "./use-hand-in-drive-chooser";

type Chosen = { file: File; contentType: string };
type Arrival = ArtifactUploadArrival | "checking" | "unavailable";
const GOOGLE_FILE_HOSTS = new Set(["drive.google.com", "docs.google.com"]);
const googleFile = (url: string) => { try { return GOOGLE_FILE_HOSTS.has(new URL(url.trim()).hostname.toLowerCase()); } catch { return false; } };
const arrivalBlocker: Partial<Record<Arrival, string>> = {
  checking: a3Copy.waitWhileYourFileIsChecked, unavailable: a3Copy.checkThatYourFileArrivedBeforeHandingIn,
  missing: a3Copy.chooseYourFileAgainBeforeHandingIn, mismatch: a3Copy.chooseYourFileAgainBeforeHandingIn,
  expired: a3Copy.thisPrivateUploadExpiredChooseYourFileAgain, discarded: a3Copy.thisPrivateUploadExpiredChooseYourFileAgain,
  finalized: a3Copy.thatUploadAlreadyBelongsToAHandInRead,
};

type Props = {
  expectedParentSubmissionId?: string | null;
  terms: AssignmentDetail; kind: "link" | "artifact"; attempt: number;
  onSubmitted: (receipt: Submission) => void; onRefused: () => void;
  renderTask: (task: WritingTaskController) => React.ReactNode;
  previousLink?: string | null; readAt?: string; allowed?: boolean;
};

/** The private reference is saved before one academic hand-in freezes its reviewed revision. */
export function HandInDeliverable(props: Props) {
  const { me } = useAuth();
  return <ReferenceHandIn key={`${me?.tenant_id}:${me?.user_id}:${props.terms.id}:${props.kind}:${props.attempt}`} {...props} />;
}

function ReferenceHandIn({ terms, kind, attempt, renderTask, previousLink, readAt, onSubmitted, onRefused, allowed = true, expectedParentSubmissionId }: Props) {
  const client = useQueryClient();
  const admitted = useAdmittedWords();
  const shut = shutWords(terms, useContext(AssignmentTimezone) ?? "UTC", "entry");
  const parent = useRef(expectedParentSubmissionId);
  allowed = allowed && expectedParentSubmissionId === parent.current;
  const draft = useReferenceDraft(terms.id, kind, attempt, allowed, kind === "link" && previousLink ? { type: "link", url: previousLink } : undefined, parent.current);
  const [result, setResult] = useState<Result>({ state: "none" });
  const [pending, setPending] = useState(false), [confirmed, setConfirmed] = useState(false);
  const [file, setFile] = useState<Chosen | null>(null), [preparing, setPreparing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null), [refusal, setRefusal] = useState<string | null>(null);
  const [prepared, setPrepared] = useState<{ id: string; name: string } | null>(null);
  const [arrival, setArrival] = useState<{ id: string; state: Arrival } | null>(null);
  const uploadKey = useRef<string | null>(null);
  const uploadFlight = useRef(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const drive = useHandInDriveChooser(kind === "link" && allowed);
  const version = [...terms.versions].sort((a, b) => b.version_no - a.version_no)[0]?.id;
  const link = draft.document?.type === "link" ? draft.document.url : "";
  const reservation = draft.document?.type === "artifact" ? draft.document.reservation_id : null;
  const metadata = draft.draft?.upload?.reservation_id === reservation ? draft.draft.upload : null;
  const name = prepared?.id === reservation ? prepared.name : metadata?.original_name;
  // A reservation saved on another device or before a reload proves nothing about its bytes.
  // Its object metadata is read once; only hand-in verifies and accepts the file.
  const recorded: Arrival | null = !metadata ? null : metadata.state === "finalized" ? "finalized" : metadata.state !== "pending" ? "discarded" : Date.parse(metadata.expires_at) <= Date.now() ? "expired" : null;
  const unproven = Boolean(reservation && metadata && !recorded && prepared?.id !== reservation);
  const arrivalState: Arrival | null = recorded ?? (unproven ? (arrival?.id === reservation ? arrival.state : "checking") : null);
  const checkArrival = useCallback(async (id: string) => {
    setArrival({ id, state: "checking" });
    try {
      const read = await assignmentsApi.readArtifactUploadArrival(terms.id, id);
      setArrival(current => current?.id === id ? { id, state: read.arrival } : current);
    } catch { setArrival(current => current?.id === id ? { id, state: "unavailable" } : current); }
  }, [terms.id]);
  useEffect(() => { if (unproven && reservation && arrival?.id !== reservation) void checkArrival(reservation); }, [unproven, reservation, arrival?.id, checkArrival]);
  let invalid = false;
  if (kind === "link" && link.trim()) {
    try { invalid = !["http:", "https:"].includes(new URL(link.trim()).protocol); } catch { invalid = true; }
  }
  const held = result.state === "unknown";
  const frozen = pending || held || confirmed || !allowed;
  const frozenNow = useRef(frozen); frozenNow.current = frozen;
  useAssignmentDraft(!confirmed && (draft.unsaved || draft.unknownSave || Boolean(file && !prepared)), !confirmed && (pending || held || preparing || draft.saving));
  const keep = (value: Result) => { setResult(value); return value; };
  const acknowledge = (receipt: Submission): Result => {
    flushSync(() => setConfirmed(true));
    client.setQueryData<Submission[]>(["assignment", terms.id, "submissions"], old => [...(old ?? []).filter(item => item.id !== receipt.id), receipt]);
    onSubmitted(receipt);
    void invalidateAssignment(client, terms.course_id, terms.id);
    return keep({ state: "confirmed", at: receipt.submitted_at, effects: [a3Copy.handedInAttempt(receipt.attempt_number), admitted(receipt)], remains: [a3Copy.yourInstructorCanNowReadThisAttempt] });
  };
  const uncertain = (key: string): Result => ({ state: "unknown", requestId: key, cap: a3Copy.handInNotConfirmed,
    sentence: a3Copy.yourWorkMayHaveArrivedCheckThisHandIn,
    check: { verb: a3Copy.checkHandIn, note: a3Copy.thisReadsTheSameHandInItNeverSends, run: async () => {
      try {
        const checked = await checkCommit<{ submission_id: string }>(key);
        if (checked.state === "confirmed") return acknowledge(await assignmentsApi.getSubmission(checked.receipt.result.submission_id));
        if (checked.state === "not_committed") return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: a3Copy.noHandInWasRecordedForThisRequestYour, draft: "kept" });
      } catch { /* Keep the original hand-in uncertain. */ }
      return keep(uncertain(key));
    } },
  });
  const prepare = async ({ file: chosen, contentType }: Chosen) => {
    if (uploadFlight.current || !version || frozen) return;
    uploadFlight.current = true; setPreparing(true); setUploadError(null); setPrepared(null);
    try {
      const digest = await crypto.subtle.digest("SHA-256", await chosen.arrayBuffer());
      const checksum = Array.from(new Uint8Array(digest), part => part.toString(16).padStart(2, "0")).join("");
      const saved = await assignmentsApi.reserveArtifactUpload(terms.id, {
        assignment_version_id: version, checksum, content_type: contentType, byte_size: chosen.size, original_name: chosen.name,
        idempotency_key: uploadKey.current ??= crypto.randomUUID(),
      });
      if (saved.state === "finalized") { throw new Error(a3Copy.thatUploadAlreadyBelongsToAHandInRead); }
      if (!saved.upload_url) throw new Error(a3Copy.thePrivateUploadCouldNotBeReservedYourFile);
      const document = { type: "artifact" as const, reservation_id: saved.reservation_id };
      draft.change(document);
      await draft.persist(document);
      const response = await fetch(saved.upload_url, { method: "PUT", headers: { "Content-Type": contentType, "x-goog-if-generation-match": "0" }, body: chosen });
      // A previous PUT may have arrived despite losing its response. The finalizer
      // verifies the recorded checksum and bytes before any academic acceptance.
      if (!response.ok && response.status !== 412) throw new Error(a3Copy.theFileUploadWasNotConfirmedYourSelectedFile);
      setPrepared({ id: saved.reservation_id, name: chosen.name });
    } catch (cause) { setUploadError(reasonOnError(cause)?.message ?? (cause instanceof Error ? cause.message : a3Copy.theFileCouldNotBePrepared)); }
    finally { uploadFlight.current = false; setPreparing(false); }
  };
  const choose = (chosen: File | null) => {
    uploadKey.current = null; setRefusal(null);
    if (!chosen) { setFile(null); return; }
    const verdict = admitFile(draft.uploadPolicy, chosen);
    // Refused before hashing or reserving: nothing leaves the browser.
    if ("refusal" in verdict) { setFile(null); setRefusal(verdict.refusal); if (fileInput.current) fileInput.current.value = ""; return; }
    const next = { file: chosen, contentType: verdict.contentType };
    setFile(next); void prepare(next);
  };
  // Removing saves the empty reference; the database abandons the earlier reservation in that save.
  const remove = () => {
    const empty: ReferenceDocument = kind === "link" ? { type: "link", url: "" } : { type: "artifact", reservation_id: null };
    setFile(null); setPrepared(null); setUploadError(null); setRefusal(null); setArrival(null); drive.clearError(); uploadKey.current = null;
    if (fileInput.current) { fileInput.current.value = ""; fileInput.current.focus(); }
    draft.change(empty);
    if (result.state === "refused") setResult({ state: "none" });
    if (draft.draft) void draft.persist(empty).catch(() => undefined);
  };
  const chooseFromDrive = async () => {
    const selection = await drive.choose();
    if (!selection || frozenNow.current) return;
    const title = selection.name.trim().slice(0, 200);
    const document: ReferenceDocument = title ? { type: "link", url: selection.fileUrl, title } : { type: "link", url: selection.fileUrl };
    draft.change(document);
    if (result.state === "refused") setResult({ state: "none" });
    void draft.persist(document).catch(() => undefined);
  };
  const blockers = (): Blocker[] => {
    const message = !allowed ? shut ?? a3Copy.thisWorkIsClosedToNewHandInsYour2
      : draft.loading ? a3Copy.readYourPrivateDraftBeforeHandingIn
      : draft.readFailed ? a3Copy.readYourCurrentPrivateDraftBeforeHandingInYour
      : draft.conflict ? a3Copy.compareYourEntryWithTheSavedReferenceFirst
      : draft.unknownSave ? a3Copy.checkYourPrivateSaveBeforeHandingIn
      : preparing ? a3Copy.waitForYourFileToBePrepared
      : uploadError ? a3Copy.retryTheSelectedFileSUploadBeforeHandingIn
      : arrivalState && arrivalBlocker[arrivalState] ? arrivalBlocker[arrivalState]
      : !version ? a3Copy.readTheCurrentWorkSTermsFirst
      : kind === "link" && !link.trim() ? a3Copy.enterYourLinkFirst
      : invalid ? a3Copy.yourLinkMustBeACompleteHttpOrHttps
      : kind === "artifact" && !reservation ? a3Copy.chooseYourFileFirst : null;
    return message ? [{ kind: "missing", part: "writing", sentence: message }] : [];
  };
  const savedReference = !confirmed && Boolean(draft.draft) && (kind === "link" ? Boolean(link) : Boolean(reservation));
  const saveStatus = draft.loading ? a3Copy.readingYourPrivateDraft : draft.saving ? a3Copy.savingYourPrivateReference : draft.unsaved || draft.unknownSave ? a3Copy.privateSaveNotConfirmedYourEntryStaysHere : draft.readFailed ? a3Copy.yourPrivateDraftIsUnavailable : savedReference ? a3Copy.savedToIMAMPrivateToYouUntilYouHand : null;
  const title = draft.document?.type === "link" ? draft.document.title : undefined;
  const editable = !frozen && !draft.loading && !draft.conflict;
  const arrivalNote = arrivalState === "checking" ? <p role="status" className="text-meta text-muted-foreground">{a3Copy.checkingThatYourFileArrived}</p>
    : arrivalState === "arrived" ? <p className="text-meta text-muted-foreground">{a3Copy.yourFileArrivedHandInVerifiesIt}</p>
    : arrivalState === "missing" || arrivalState === "mismatch" ? <p role="alert">{a3Copy.uploadNotConfirmedChooseYourFileAgain}</p>
    : arrivalState === "unavailable" ? <><p role="alert">{a3Copy.yourFileCouldNotBeCheckedYourReferenceIsKept}</p>
      <Button variant="outline" onClick={() => reservation && void checkArrival(reservation)}>{a3Copy.checkFileAgain}</Button></>
    : arrivalState === "expired" || arrivalState === "discarded" ? <p>{a3Copy.thisPrivateUploadExpiredChooseYourFileAgain}</p>
    : arrivalState === "finalized" ? <p>{a3Copy.thatUploadAlreadyBelongsToAHandInRead}</p> : null;
  const editor = <div className="grid gap-2">
    {kind === "link" ? <>
      {title && <p dir="auto">{title}</p>}
      <Input aria-label={a3Copy.yourLink} type="url" dir="ltr" value={link} aria-invalid={invalid || undefined} disabled={frozen || draft.loading}
        onChange={event => { draft.change({ type: "link", url: event.target.value }); drive.clearError(); if (result.state === "refused") setResult({ state: "none" }); }}
        onBlur={() => { if (!frozen && !draft.conflict && draft.unsaved) void draft.persist().catch(() => undefined); }} />
      <p className="text-meta text-muted-foreground">{googleFile(link) ? a3Copy.googleDecidesWhoCanOpenThisFileYourTeaching : a3Copy.yourTeachingTeamMustBeAbleToOpenThisLink}</p>
      {(drive.enabled || (link && editable)) && <div className="flex flex-wrap gap-2">
        {drive.enabled && <Button ref={drive.triggerRef} type="button" variant="outline" disabled={!editable || drive.busy} onClick={() => void chooseFromDrive()}>
          {drive.busy ? a3Copy.openingGoogleDrive : a3Copy.chooseFromGoogleDrive}</Button>}
        {drive.busy && <Button type="button" variant="ghost" onClick={drive.cancel}>{a3Copy.cancelOpeningGoogleDrive}</Button>}
        {link && editable && !drive.busy && <Button type="button" variant="ghost" onClick={remove}>{a3Copy.removeLink}</Button>}
      </div>}
      {drive.error && <p role="alert">{drive.error}</p>}
    </> : <>
      <Input ref={fileInput} aria-label={a3Copy.yourFile} type="file" accept={acceptAttribute(draft.uploadPolicy)} disabled={frozen || preparing || draft.loading || Boolean(draft.conflict)}
        onChange={event => choose(event.target.files?.[0] ?? null)} />
      {draft.uploadPolicy && <p className="text-meta text-muted-foreground">{uploadLimits(draft.uploadPolicy)}</p>}
      {name && <p>{name}</p>}
      {preparing && <p className="text-meta text-muted-foreground">{a3Copy.preparingYourPrivateFile}</p>}
      {!preparing && arrivalNote}
      {refusal && <p role="alert">{refusal}</p>}
      {(reservation || file) && editable && !preparing && <Button type="button" variant="ghost" className="justify-self-start" onClick={remove}>{a3Copy.removeFile}</Button>}
    </>}
    {saveStatus && <p role="status" className="text-meta text-muted-foreground">{saveStatus}</p>}
    {(draft.error || uploadError) && <p role="alert">{uploadError ?? draft.error}</p>}
    {draft.readFailed && <Button variant="outline" disabled={frozen} onClick={() => void draft.load()}>{a3Copy.readPrivateDraftAgain}</Button>}
    {draft.unknownSave && <Button variant="outline" disabled={frozen || draft.saving} onClick={() => void draft.checkSaved()}>{a3Copy.checkPrivateSave}</Button>}
    {draft.unknownSave && <Button variant="outline" disabled={frozen || draft.saving} onClick={() => void draft.persist().catch(() => undefined)}>{a3Copy.retryPrivateSave}</Button>}
    {uploadError && file && <Button variant="outline" disabled={frozen || preparing} onClick={() => void prepare(file)}>{a3Copy.retryFileUpload}</Button>}
    {draft.conflict && <section aria-label={a3Copy.savedReferenceChanged} className="grid gap-2">
      <p>{a3Copy.theSavedReferenceChangedYourEntryIsKeptCompare}</p>
      <p dir="ltr">{draft.conflict.document.type === "link" ? draft.conflict.document.url : draft.conflict.upload?.original_name ?? a3Copy.anotherPrivateFileReference}</p>
      <div className="flex gap-2"><Button variant="outline" disabled={frozen} onClick={draft.keepMine}>{a3Copy.keepMyEntry}</Button><Button variant="outline" disabled={frozen} onClick={draft.useRecorded}>{a3Copy.useSavedReference}</Button></div>
    </section>}
    {invalid && <p>{a3Copy.yourLinkMustBeACompleteHttpOrHttps}</p>}
  </div>;
  return renderTask({ editor, read: draft.loading && !draft.draft ? { status: "unread", sentence: a3Copy.yourPrivateDraftHasNotBeenRead }
    : { status: "ready", value: null, readAt: readAt ?? terms.updated_at }, draft: !confirmed && draft.unsaved, pending, result,
    commit: { verb: a3Copy.handInAttempt(attempt), cap: a3Copy.whenYouHandIn, effects: () => [{ id: "hand-in", sentence: kind === "link"
      ? previousLink && link.trim() === previousLink.trim() ? a3Copy.handInAttemptWithTheSameLinkYourTeacher(attempt) : a3Copy.sendTheSavedLinkShownHereToYourInstructor
      : a3Copy.sendToYourInstructorForReview(name ?? a3Copy.thePrivateFile) }], blockers,
      run: async key => {
        if (blockers().length) return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: a3Copy.resolveTheHandInSBlockerFirst, draft: "kept" });
        setPending(true);
        let saved: ReferenceDraft;
        try { saved = await draft.persist(); }
        catch { setPending(false); return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: a3Copy.yourPrivateSaveMustBeConfirmedFirstYourEntry, draft: "kept" }); }
        const review = { draft_id: saved.id, expected_revision: saved.revision, content_digest: saved.content_digest, expected_return_event_id: saved.review_return_event_id ?? null };
        try {
          return acknowledge(kind === "link"
            ? await assignmentsApi.submitReference(terms.id, { ...review, operation_id: crypto.randomUUID(), assignment_version_id: version! }, key)
            : await assignmentsApi.finalizeArtifactUpload(terms.id, (saved.document as { reservation_id: string }).reservation_id, key, review));
        } catch (cause) {
          if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && cause.status !== 408) {
            onRefused(); return keep({ state: "refused", cap: a3Copy.notHandedIn, sentence: reasonOnError(cause)?.message ?? cause.message, draft: "kept" });
          }
          return keep(uncertain(key));
        } finally { setPending(false); }
      } },
  });
}
